import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UtilService } from 'src/app/core/services';
import { AuthService } from 'src/app/core/services/auth/auth.service';

/**
 * Name of the window that runs the game: the desktop popup and, on mobile, the tab that
 * shows /spielen (set by the game shell). window.open with this name reuses and focuses
 * that window instead of opening a second game.
 */
export const GAME_WINDOW_NAME = 'schnapsen-spiel';

const GAME_POPUP_FEATURES =
  'resizable=yes, toolbar=no, scrollbars=no, menubar=no, status=no, directories=no, location=no, width=965, height=750';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private popupWindow: any;

  constructor(
    private authService: AuthService,
    private router: Router,
    private utilService: UtilService
  ) {}

  public async startGame(): Promise<void> {
    if (await this.authService.isAuthenticated()) {
      if (this.utilService.isMobile()) {
        this.router.navigate(['/spielen']);
      } else {
        // an open game window comes to the front with its game, it is not reloaded
        this.popupWindow = this.showGameWindow('/spielen');
      }
    } else {
      this.router.navigate(['/login']);
    }
  }

  public async startTrainingGame(numPlayers: 2 | 3 | 4 = 2, difficulty: 'easy' | 'medium' | 'hard' | 'expert' = 'medium'): Promise<void> {
    if (await this.authService.isAuthenticated()) {
      const queryParams = {
        training: 'true',
        players: numPlayers.toString(),
        difficulty: difficulty
      };
      
      if (this.utilService.isMobile()) {
        this.router.navigate(['/spielen'], { queryParams });
      } else {
        const url = `/spielen?training=true&players=${numPlayers}&difficulty=${difficulty}`;
        this.popupWindow = window.open(
          url,
          GAME_WINDOW_NAME,
          GAME_POPUP_FEATURES
        );
      }
    } else {
      this.router.navigate(['/login']);
    }
  }

  public async createPrivateTable(numPlayers: 2 | 3 | 4 = 2): Promise<void> {
    if (await this.authService.isAuthenticated()) {
      const queryParams = {
        createPrivate: 'true',
        players: numPlayers.toString()
      };
      
      if (this.utilService.isMobile()) {
        this.router.navigate(['/spielen'], { queryParams });
      } else {
        const url = `/spielen?createPrivate=true&players=${numPlayers}`;
        this.popupWindow = window.open(
          url,
          GAME_WINDOW_NAME,
          GAME_POPUP_FEATURES
        );
      }
    } else {
      this.router.navigate(['/login']);
    }
  }

  public async startQuickMatch(numPlayers: 2 | 3 | 4 = 2): Promise<void> {
    if (await this.authService.isAuthenticated()) {
      const queryParams = {
        quickMatch: 'true',
        players: numPlayers.toString()
      };
      
      if (this.utilService.isMobile()) {
        this.router.navigate(['/spielen'], { queryParams });
      } else {
        const url = `/spielen?quickMatch=true&players=${numPlayers}`;
        this.popupWindow = window.open(
          url,
          GAME_WINDOW_NAME,
          GAME_POPUP_FEATURES
        );
      }
    } else {
      this.router.navigate(['/login']);
    }
  }

  /**
   * The Abo page belongs in the main window, not in the small game popup: the popup
   * hands it over and closes. Only used outside a running game.
   */
  public showPremium(win: Window = window): void {
    const opener = win.opener as Window | null;
    if (!opener) {
      void this.router.navigateByUrl('/premium'); // mobile, or the game opened directly
    } else if (opener.closed) {
      win.open('/premium', '_blank'); // the main window is gone
    } else {
      opener.location.href = '/premium';
      opener.focus();
      win.close();
    }
  }

  /**
   * Back to the running game from another tab (e.g. the Spielprotokoll link in the chat).
   * Never closes this tab and never reloads the game (a reload would lose what is on
   * screen). First tries to bring the game window that opened this tab to the front;
   * browsers may refuse that silently, so it checks that this tab lost focus. Otherwise
   * it addresses the game window by name, see showGameWindow.
   */
  public async returnToGame(win: Window = window): Promise<void> {
    if (!(await this.authService.isAuthenticated())) {
      void this.router.navigate(['/login']);
      return;
    }
    const opener = this.gameOpener(win);
    if (opener) {
      const lostFocus = this.waitForFocusLoss(win, 400);
      opener.focus();
      if (await lostFocus) return;
    }
    if (this.utilService.isMobile() && !opener) {
      // no game tab we could reach: play here instead of opening another tab
      void this.router.navigate(['/spielen']);
      return;
    }
    this.showGameWindow('/spielen', win);
  }

  /**
   * Brings the game window to the front without reloading it: window.open without a URL
   * only addresses the window named GAME_WINDOW_NAME. If there is none, the browser opens
   * an empty one, which then loads `url`.
   */
  private showGameWindow(url: string, win: Window = window): Window | null {
    const features = this.utilService.isMobile() ? undefined : GAME_POPUP_FEATURES;
    const game = win.open('', GAME_WINDOW_NAME, features);
    if (!game) return null; // blocked
    let isNew: boolean;
    try {
      isNew = game.location.href === 'about:blank';
    } catch {
      isNew = false; // not ours to inspect: leave it alone
    }
    if (isNew) game.location.href = url;
    game.focus();
    return game;
  }

  /** the window that opened this tab, if it is a live game window */
  private gameOpener(win: Window): Window | null {
    try {
      const opener = win.opener as Window | null;
      if (!opener || opener.closed) return null;
      return opener.name === GAME_WINDOW_NAME || opener.location.pathname.startsWith('/spielen') ? opener : null;
    } catch {
      return null; // other origin
    }
  }

  private waitForFocusLoss(win: Window, ms: number): Promise<boolean> {
    return new Promise(resolve => {
      const done = (lost: boolean) => {
        win.removeEventListener('blur', onBlur);
        win.document.removeEventListener('visibilitychange', onBlur);
        clearTimeout(timer);
        resolve(lost);
      };
      const onBlur = () => done(true);
      const timer = setTimeout(() => done(!win.document.hasFocus() || win.document.hidden), ms);
      win.addEventListener('blur', onBlur);
      win.document.addEventListener('visibilitychange', onBlur);
    });
  }

  public showPlayerInfo(userID: number) {
    if (this.utilService.isMobile()) {
      this.router.navigateByUrl('/profil/' + userID);
    } else {
      window.open('/profil/' + userID, '_blank');
    }
  }
}
